import { useCallback, useEffect, useRef, useState } from "react";
import type {
  MouseEvent as ReactMouseEvent,
  PointerEvent as ReactPointerEvent,
  RefObject,
} from "react";

import { DRAG_THRESHOLD } from "./drag-gesture";

/**
 * Moving around a drawing that is bigger than the window it is in, by dragging the drawing
 * itself.
 *
 * The window is scrolled rather than the drawing moved, so nothing about where a mark sits
 * changes: every point is a fraction of a box that is measured on each event, and a box
 * that has been scrolled reports where it now is.
 *
 * The press is shared with the drawing beneath it. Under the threshold it is still the
 * click it was, selecting, deselecting or placing; past it the window moves and the click
 * the browser fires afterwards chooses nothing. That is the same rule the mark drags
 * follow, and the same three pixels, because an attorney's hand does not know which of the
 * two it is starting.
 */

/** A pan under way, and where the window stood when it started. */
interface Grip {
  /** The pointer that started this, so a second finger cannot move or finish it. */
  pointerId: number;
  from: { x: number; y: number };
  at: { left: number; top: number };
}

/** Whether there is anywhere to drag to, which is what arms the gesture. */
function overflows(view: HTMLElement): boolean {
  return (
    view.scrollWidth > view.clientWidth || view.scrollHeight > view.clientHeight
  );
}

/** A press that belongs to something else: a pin, a handle, a control on the drawing. */
function onAControl(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    target.closest('button, input, [contenteditable="true"]') !== null
  );
}

/**
 * The wheel is not how the drawing moves.
 *
 * A wheel over the artwork would scroll the window under the pointer while the attorney is
 * aiming a numeral at it, which is the one place on this screen where a few pixels of
 * unasked-for movement costs a mark in the wrong place. Dragging says where the drawing
 * goes, and says it deliberately.
 *
 * Bound by hand rather than with `onWheel`, because a wheel listener React attaches is
 * passive and a passive listener cannot refuse the scroll it was told about.
 */
function useWheelHeld(view: RefObject<HTMLElement | null>, held: boolean) {
  useEffect(() => {
    const window = view.current;
    if (window === null || !held) return undefined;
    function refuse(event: WheelEvent) {
      event.preventDefault();
    }
    window.addEventListener("wheel", refuse, { passive: false });
    return () => {
      window.removeEventListener("wheel", refuse);
    };
  }, [view, held]);
}

/**
 * Dragging the window's contents, for as long as they do not fit inside it.
 *
 * The moves and the release are watched on the window rather than captured on the drawing,
 * because a pointer that has left the artwork is still moving the drawing it picked up.
 */
export function usePan(view: RefObject<HTMLElement | null>, pannable: boolean) {
  useWheelHeld(view, pannable);
  const gripRef = useRef<Grip | undefined>(undefined);
  const [live, setLive] = useState(false);
  const [moving, setMoving] = useState(false);
  // Whether the click now arriving is the tail of a pan rather than a choice. Held in a ref
  // because it is read when that click lands, not when the page last drew.
  const pannedRef = useRef(false);

  const letGo = useCallback(() => {
    gripRef.current = undefined;
    setLive(false);
    setMoving(false);
  }, []);

  useEffect(() => {
    if (!live) return undefined;

    function moved(event: PointerEvent) {
      const held = gripRef.current;
      const window = view.current;
      if (held === undefined || window === null) return;
      if (event.pointerId !== held.pointerId) return;
      const by = {
        x: event.clientX - held.from.x,
        y: event.clientY - held.from.y,
      };
      // Said once, on the move that turns the press into a pan. Every move after it is
      // already a pan, and re-saying so would allocate and dispatch sixty times a second.
      if (!pannedRef.current) {
        if (Math.hypot(by.x, by.y) < DRAG_THRESHOLD) return;
        pannedRef.current = true;
        setMoving(true);
      }
      window.scrollLeft = held.at.left - by.x;
      window.scrollTop = held.at.top - by.y;
    }

    function released(event: PointerEvent) {
      if (gripRef.current?.pointerId === event.pointerId) letGo();
    }

    function taken(event: PointerEvent) {
      // The platform taking the pointer away ends the pan where it stands. There is
      // nothing to undo: the window is already where the attorney dragged it to.
      if (gripRef.current?.pointerId === event.pointerId) letGo();
    }

    globalThis.addEventListener("pointermove", moved);
    globalThis.addEventListener("pointerup", released);
    globalThis.addEventListener("pointercancel", taken);
    return () => {
      globalThis.removeEventListener("pointermove", moved);
      globalThis.removeEventListener("pointerup", released);
      globalThis.removeEventListener("pointercancel", taken);
    };
  }, [live, view, letGo]);

  return {
    /**
     * What the pointer says a press will do, for the drawing to wear.
     *
     * Nothing until there is somewhere to drag to. The cursor is inherited, so whatever
     * covers the artwork has the last word by saying anything at all, and this shows
     * through wherever nothing does: the annotation layer's crosshair, a pin's grab.
     */
    cursor: !pannable ? "" : moving ? "cursor-grabbing" : "cursor-grab",

    /**
     * Swallows the click that trails a pan, before whatever is over the drawing hears it.
     *
     * Caught on the way down, because the click lands on the annotation layer and the
     * layer reading it as a click on the drawing is the thing being prevented: the
     * attorney was moving the drawing, not choosing a place on it.
     */
    swallow: (event: ReactMouseEvent<HTMLElement>) => {
      if (pannedRef.current) event.stopPropagation();
    },

    /**
     * Starts a pan, on the way down to whatever was pressed.
     *
     * Caught in the capture phase because a pin stops the press reaching anything above
     * it, and the flag below has to be cleared by every press regardless: a pan whose
     * release landed off the drawing leaves no click behind to spend it on.
     */
    begin: (event: ReactPointerEvent<HTMLElement>) => {
      pannedRef.current = false;
      if (!pannable) return;
      const window = view.current;
      if (window === null || !overflows(window)) return;
      // A press on a pin, a handle or any other control on the drawing is that control's,
      // not the window's: dragging a numeral must never move the page under it instead.
      if (onAControl(event.target)) return;
      // The drag belongs to the window now, so the browser must not also read it as a
      // text selection: a pan that leaves the drawing would otherwise sweep a highlight
      // across the toolbar and the registry beside it.
      event.preventDefault();
      gripRef.current = {
        pointerId: event.pointerId,
        from: { x: event.clientX, y: event.clientY },
        at: { left: window.scrollLeft, top: window.scrollTop },
      };
      setLive(true);
    },
  };
}
